import type {
  WFSGetFeatureParams,
  WMSFeatureInfoParams,
} from "./services/ogc.service";
import type { PoiTimeseriesType } from "./types";

export const baseDataKeys = {
  residentialMaps: (runYear: number) => ["residentialMaps", runYear],
  languages: ["languages"],
  permissions: ["permissions"],
  regions: ["regions"],
  roadTypes: ["roadTypes"],
  srids: ["srids"],
  substances: ["substances"],
  templates: ["templates"],
  extraLayers: ["extraLayers"],
  atmoStreetLayers: (runYear: number) => ["atmoStreetLayers", runYear],
  stations: ["statons"],
  predefinedLEZs: (runYear: number) => ["predefinedLEZs", runYear],
};

export const caseKeys = {
  all: ["cases"],
  detail: (caseId: string | number) => ["cases", caseId],
  info: (caseId: string | number) => ["cases", caseId, "info"],
  regularGrid: (caseId: string | number) => ["cases", caseId, "regularGrid"],
  lineBasedGrid: (caseId: string | number) => [
    "cases",
    caseId,
    "lineBasedGrid",
  ],
  pointBasedGrid: (caseId: string | number) => [
    "cases",
    caseId,
    "pointBasedGrid",
  ],
  result: (caseId: string | number) => ["cases", caseId, "result"],
  resultDownload: (caseId: string | number) => [
    "cases",
    caseId,
    "resultDownload",
  ],
};
export const scenarioKeys = {
  all: (caseId: string | number) => ["cases", caseId, "scenarios"],
  detail: (scenarioId: string | number) => ["scenarios", scenarioId],
  vehicleTypes: (scenarioId: string | number) => [
    "scenarios",
    scenarioId,
    "vehicleTypes",
  ],
};

export const poiKeys = {
  all: (caseId: string | number) => ["cases", caseId, "pois"],
  detail: (caseId: string | number, poiId: string | number) => [
    "cases",
    caseId,
    "pois",
    poiId,
  ],
  timeseries: (
    scenarioId: string | number,
    poiId: string | number,
    substanceId: string | number,
    type: PoiTimeseriesType
  ) => [
    "scenarios",
    scenarioId,
    "pois",
    poiId,
    "timeseries",
    substanceId,
    type,
  ],
};

export const residentialZoneKeys = {
  all: (scenarioId: string | number) => [
    "scenarios",
    scenarioId,
    "residentialZones",
  ],
  detail: (scenarioId: string | number, zoneId: string | number) => [
    "scenarios",
    scenarioId,
    "residentialZones",
    zoneId,
  ],
};

export const segmentKeys = {
  all: (scenarioId: string | number) => ["scenarios", scenarioId, "segments"],
  detail: (scenarioId: string | number, segmentId: string | number) => [
    "scenarios",
    scenarioId,
    "segments",
    segmentId,
  ],
};

export const backgroundConcentrationKeys = {
  all: ["backgroundConcentrations"],
  detail: (bcId: string | number) => ["backgroundConcentrations", bcId],
};

export const customMeteoKeys = {
  all: ["customMeteo"],
  detail: (meteoId: string | number) => ["customMeteo", meteoId],
};

export const emissionKeys = {
  all: (scenarioId: string | number) => ["scenarios", scenarioId, "emissions"],
  detail: (scenarioId: string | number, emissionId: string | number) => [
    "scenarios",
    scenarioId,
    "emissions",
    emissionId,
  ],
};

export const pointSourceEmissionKeys = {
  detail: (
    scenarioId: string | number,
    pointSourceEmissionId: string | number
  ) => ["scenarios", scenarioId, "pointSourceEmissions", pointSourceEmissionId],
};

export const lezKeys = {
  all: (scenarioId: string | number) => ["scenarios", scenarioId, "lezs"],
  detail: (scenarioId: string | number, lezId: string | number) => [
    "scenarios",
    scenarioId,
    "lezs",
    lezId,
  ],
  fleetConfig: (lezId: string | number) => ["lezs", lezId, "fleetConfig"],
  predefinedLEZs: (caseId: string | number) => ["predefinedLEZs", caseId],
  percentageFleets: (caseId: string | number) => ["percentageFleets", caseId],
  restrictionsFleets: (caseId: string | number) => [
    "restrictionsFleets",
    caseId,
  ],
  fleetPercentageConfig: (lezId: string | number) => [
    "lezs",
    lezId,
    "fleetPercentageConfig",
  ],
};

export const ogcKeys = {
  wmsFeatureInfo: (params: WMSFeatureInfoParams) => [
    "wms-feature-info",
    JSON.stringify(params),
  ],
  wfsGetFeature: (params: WFSGetFeatureParams) => [
    "wfs-get-feature",
    JSON.stringify(params),
  ],
};

export const pointSourceKeys = {
  detail: (
    scenarioId: number | string,
    emissionId: number | string,
    pointSourceId: number | string
  ) => [
    "scenarios",
    scenarioId,
    "emissions",
    emissionId,
    "pointSources",
    pointSourceId,
  ],
};

export const userKeys = {
  all: ["users"],
  detail: (userId: string | number) => ["users", userId],
};

export const legendKeys = {
  detail: (path: string) => ["legend", path],
};
